'use client';

import * as React from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { clsx } from 'clsx';
import styles from './Dialog.module.css';

/* ===========================================
   DIALOG ROOT
   =========================================== */

export interface DialogProps {
  /**
   * Whether the dialog is open
   */
  open?: boolean;

  /**
   * Default open state (uncontrolled)
   */
  defaultOpen?: boolean;

  /**
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Modal behavior
   * - true: Focus trap + scroll lock + pointer blocking
   * - 'trap-focus': Focus trapped, scroll/pointer enabled
   * - false: No restrictions
   */
  modal?: boolean | 'trap-focus';

  children?: React.ReactNode;
}

export function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  modal = true,
  children,
}: DialogProps) {
  return (
    <BaseDialog.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
    >
      {children}
    </BaseDialog.Root>
  );
}

/* ===========================================
   DIALOG TRIGGER
   =========================================== */

export interface DialogTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children?: React.ReactNode;
  asChild?: boolean;
}

export const DialogTrigger = React.forwardRef<HTMLButtonElement, DialogTriggerProps>(
  ({ className, children, ...props }, ref) => (
    <BaseDialog.Trigger ref={ref} className={className} {...props}>
      {children}
    </BaseDialog.Trigger>
  )
);

DialogTrigger.displayName = 'DialogTrigger';

/* ===========================================
   DIALOG PORTAL
   =========================================== */

export interface DialogPortalProps {
  children?: React.ReactNode;
  container?: HTMLElement | null;
}

export function DialogPortal({ children, container }: DialogPortalProps) {
  return <BaseDialog.Portal container={container}>{children}</BaseDialog.Portal>;
}

/* ===========================================
   DIALOG BACKDROP
   =========================================== */

export interface DialogBackdropProps {
  className?: string;
}

export function DialogBackdrop({ className }: DialogBackdropProps) {
  return <BaseDialog.Backdrop className={clsx(styles.backdrop, className)} />;
}

/* ===========================================
   DIALOG CONTENT
   =========================================== */

export interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Size of the dialog
   * - sm: 400px max width
   * - md: 500px max width (default)
   * - lg: 640px max width
   * - xl: 800px max width
   * - full: Nearly full screen
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';

  className?: string;
  children?: React.ReactNode;
}

export const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  ({ size = 'md', className, children, ...props }, ref) => {
    const [el, setEl] = React.useState<HTMLDivElement | null>(null);

    const handleRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        setEl(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref]
    );

    React.useEffect(() => {
      if (!el) return;
      const id = setTimeout(() => {
        const first = el.querySelector<HTMLElement>(
          'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
        );
        first?.focus();
      }, 0);
      return () => clearTimeout(id);
    }, [el]);

    return (
      <BaseDialog.Popup
        ref={handleRef}
        className={clsx(styles.content, styles[`size-${size}`], className)}
        {...props}
      >
        {children}
      </BaseDialog.Popup>
    );
  }
);

DialogContent.displayName = 'DialogContent';

/* ===========================================
   DIALOG HEADER
   =========================================== */

export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.header, className)} {...props}>
      {children}
    </div>
  )
);

DialogHeader.displayName = 'DialogHeader';

/* ===========================================
   DIALOG TITLE
   =========================================== */

export interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  className?: string;
}

export const DialogTitle = React.forwardRef<HTMLHeadingElement, DialogTitleProps>(
  ({ className, children, ...props }, ref) => (
    <BaseDialog.Title ref={ref} className={clsx(styles.title, className)} {...props}>
      {children}
    </BaseDialog.Title>
  )
);

DialogTitle.displayName = 'DialogTitle';

/* ===========================================
   DIALOG DESCRIPTION
   =========================================== */

export interface DialogDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  className?: string;
}

export const DialogDescription = React.forwardRef<HTMLParagraphElement, DialogDescriptionProps>(
  ({ className, children, ...props }, ref) => (
    <BaseDialog.Description ref={ref} className={clsx(styles.description, className)} {...props}>
      {children}
    </BaseDialog.Description>
  )
);

DialogDescription.displayName = 'DialogDescription';

/* ===========================================
   DIALOG BODY
   =========================================== */

export interface DialogBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const DialogBody = React.forwardRef<HTMLDivElement, DialogBodyProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.body, className)} {...props}>
      {children}
    </div>
  )
);

DialogBody.displayName = 'DialogBody';

/* ===========================================
   DIALOG FOOTER
   =========================================== */

export interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.footer, className)} {...props}>
      {children}
    </div>
  )
);

DialogFooter.displayName = 'DialogFooter';

/* ===========================================
   DIALOG CLOSE
   =========================================== */

export interface DialogCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
}

export const DialogClose = React.forwardRef<HTMLButtonElement, DialogCloseProps>(
  ({ className, children, ...props }, ref) => (
    // aria-label before the spread so an explicit one from the caller wins.
    <BaseDialog.Close
      ref={ref}
      aria-label="Close"
      className={clsx(styles.close, className)}
      {...props}
    >
      {children || (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      )}
    </BaseDialog.Close>
  )
);

DialogClose.displayName = 'DialogClose';

export default Dialog;
